import { useMutation } from '@apollo/client';
import {
  BottomNavigation,
  Button,
  Callout,
  Controller,
  Input,
  PageModal,
  TextEditor,
  Typography,
  useForm,
} from '@otp/ui';
import { DocumentNode } from 'graphql';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UsernameEmail } from 'src/components/manage-otps/list/table/columns/username-email';
import {
  CreateAndApproveUserMergeRequestDocument,
  UserMergeRequestReason,
  UserMergeRequestStatus,
} from 'src/graphql/generated/graphql';
import { useUsers } from 'src/hooks/query/use-users';

type Form = {
  originalUserEid: string;
  newUserEid: string;
  note: string;
};

interface CreateAndApproveFormProps {
  onClose: () => void;
  onFinish: () => void;
  refetchQueries?: DocumentNode[];
}

export const CreateAndApproveForm = ({
  onClose,
  onFinish,
  refetchQueries,
}: CreateAndApproveFormProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'user-merge-requests.form',
  });

  const [createAndApproveUserMergeRequest, { loading }] = useMutation(
    CreateAndApproveUserMergeRequestDocument,
    {
      refetchQueries,
    },
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    trigger,
  } = useForm<Form>();

  const originalUserEid = watch('originalUserEid');
  const { users: originalUsers } = useUsers({
    filter: {
      mcId: originalUserEid,
    },
    enabled: !!originalUserEid,
  });

  const newUserEid = watch('newUserEid');
  const { users: newUsers } = useUsers({
    filter: {
      mcId: newUserEid,
    },
    enabled: !!newUserEid,
  });

  useEffect(() => {
    if (originalUsers?.length || newUsers?.length) {
      trigger();
    }
  }, [originalUsers, newUsers, trigger]);

  const onSubmit = async (form: Form) => {
    await createAndApproveUserMergeRequest({
      variables: {
        data: {
          userId: originalUsers[0].id,
          eIdsToMerge: [form.newUserEid],
          reason: UserMergeRequestReason.Other,
          requestNotes: form.note,
          status: UserMergeRequestStatus.Pending,
          previousStoreNumbers: [],
        },
      },
    });

    onFinish();
  };

  return (
    <PageModal open onClose={onClose} title={t('title-merge-accounts')}>
      <Callout severity="info" className="mb-2">
        {t('description-merge-accounts')}
      </Callout>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="originalUserEid"
          control={control}
          rules={{
            required: true,
            validate: (value) => {
              if (!originalUsers?.length) {
                return t('validation.user-not-found');
              }

              return true;
            },
          }}
          render={({ field }) => (
            <div className="mb-2">
              <Input
                label={t('fields.original-user-eid')}
                placeholder={t('fields.original-user-eid')}
                error={!!errors.originalUserEid}
                helperText={errors.originalUserEid?.message}
                {...field}
                ref={undefined}
                value={undefined}
                inputRef={field.ref}
                required
              />
              <UsernameEmail
                className="mt-1 mb-3"
                user={originalUsers?.[0]}
                options={{ withLevel: true }}
              />
            </div>
          )}
        />

        <Controller
          name="newUserEid"
          control={control}
          rules={{
            required: true,
            validate: (value) => {
              if (!newUsers?.length) {
                return t('validation.user-not-found');
              }

              if (originalUserEid === value) {
                return t('validation.same-user');
              }

              return true;
            },
          }}
          render={({ field }) => (
            <div className="mb-2">
              <Input
                label={t('fields.new-user-eid')}
                placeholder={t('fields.new-user-eid')}
                error={!!errors.newUserEid}
                helperText={errors.newUserEid?.message}
                {...field}
                ref={undefined}
                value={undefined}
                inputRef={field.ref}
                required
              />
              <UsernameEmail
                className="mt-1 mb-3"
                user={newUsers?.[0]}
                options={{ withLevel: true }}
              />
            </div>
          )}
        />

        <Controller
          name="note"
          control={control}
          rules={{ required: false }}
          render={({ field }) => (
            <div className="mb-2">
              <Typography variant="subtitle1">{t('fields.note')}</Typography>
              <TextEditor {...field} />
            </div>
          )}
        />

        <BottomNavigation>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {t('merge')}
          </Button>
          <Button variant="outlined" color="primary" onClick={onClose}>
            {t('cancel')}
          </Button>
        </BottomNavigation>
      </form>
    </PageModal>
  );
};
